import type { MetaFunction } from 'react-router';

import CalendarIcon from '~/assets/icons/Calendar';
import FlameIcon from '~/assets/icons/Flame';
import PercentIcon from '~/assets/icons/Percent';
import RestockIcon from '~/assets/icons/Restock';
import SaleIcon from '~/assets/icons/Sale';
import StarFullIcon from '~/assets/icons/StarFull';
import { DownloadFromAppStoreLink, Stars } from '~/components/DownloadAppBlock';
import Image from '~/components/Image';
import Tracked from '~/components/Tracked';
import useEnv from '~/hooks/useEnv';
import useViewEvent from '~/hooks/useViewEvent';

const APPS_FLYER_LINK = 'https://thesolesupplier.onelink.me/BPkz/es4ex7xo';

function TitleAndDescription() {
  return (
    <div className="flex flex-col items-start justify-center bg-black">
      <div className="flex flex-col ">
        <p className="text-title-xl text-golden-syrup">Unlock more features</p>
        <p className="text-[32px] text-white lg:text-[40px]">on our app.</p>
      </div>
    </div>
  );
}

export function RatingsBlock() {
  const env = useEnv();

  return (
    <div
      className={`flex items-center justify-start gap-2 rounded bg-black text-center md:w-fit lg:flex-col`}
    >
      <div className="h-[24px] w-[84px] lg:hidden">
        <DownloadFromAppStoreLink
          href={APPS_FLYER_LINK}
          pagePosition="Body"
          placement="app_block"
        />
      </div>
      <div className="flex w-[106px] items-center gap-2 text-white lg:hidden">
        <Stars />
        <p>{env.APP_RATING}</p>
      </div>
    </div>
  );
}

function MobileAppImage() {
  return (
    <>
      <img
        className="flex w-80 md:hidden lg:hidden"
        alt="notification-centre-mobile"
        src="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/notification-centre-mobile.png"
      />
      <img
        className="mr-12 hidden h-[526px] w-auto md:flex lg:hidden"
        alt="notification-centre-small-desktop"
        src="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/notification-centre-small-desktop.png"
      />
      <img
        className="hidden h-[547px] w-auto lg:flex xl:hidden"
        alt="notification-centre-medium-desktop"
        src="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/notification-centre-medium-desktop.png"
      />
      <img
        className="hidden h-[547px] w-auto xl:flex"
        alt="notification-centre-large-desktop"
        src="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/notification-centre-large-desktop.png"
      />
    </>
  );
}

type BenefitProps = {
  icon: React.ReactNode;
  text: string;
};

function Benefit({ icon, text }: BenefitProps) {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <p className="font-normal text-white">{text}</p>
    </div>
  );
}

function Benefits() {
  return (
    <div className="flex flex-col gap-4">
      <Benefit
        icon={
          <FlameIcon
            fill="none"
            stroke="#FA0"
            strokeWidth={2}
            height={16}
            width={16}
          />
        }
        text={'Latest releases. First.'}
      />
      <Benefit
        icon={<RestockIcon stroke="#FA0" height={16} width={16} />}
        text={'Instant restock alerts.'}
      />
      <Benefit
        icon={<PercentIcon height={16} width={16} />}
        text={'Exclusive codes.'}
      />
      <Benefit
        icon={<SaleIcon stroke="#FA0" height={16} width={16} />}
        text={'The best deals & discounts.'}
      />
      <Benefit
        icon={<CalendarIcon stroke="#FA0" height={16} width={16} />}
        text={'Upcoming release reminders.'}
      />
      <p className="text-base text-golden-syrup">
        Instant notifications or read later in the app.
      </p>
    </div>
  );
}

function StarRating() {
  const env = useEnv();

  return (
    <div className="flex  w-full flex-row text-xs text-white">
      <StarFullIcon fill="#FA0" />
      <StarFullIcon fill="#FA0" />
      <StarFullIcon fill="#FA0" />
      <StarFullIcon fill="#FA0" />
      <StarFullIcon fill="#FA0" />
      <p className="pl-2">{env.APP_RATING}</p>
    </div>
  );
}

function DownloadFromAppStore() {
  return (
    <>
      <Image
        className="w-full lg:flex"
        alt="notification-centre-medium-desktop"
        src="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/notification-centre-qr-code.png"
      />
      <DownloadFromAppStoreLink
        href={APPS_FLYER_LINK}
        pagePosition="Body"
        placement="app_block"
      />
    </>
  );
}

export default function NotificationCentre() {
  useViewEvent({ viewType: 'notification_center' });

  return (
    <div className="flex justify-center bg-grey-panel py-12 md:pb-0">
      <div className="flex flex-col items-center justify-center bg-grey-panel md:flex-row">
        <MobileAppImage />
        <div className="flex flex-col justify-center lg:gap-6">
          <div className="flex flex-col gap-4 bg-black p-6 leading-none">
            <TitleAndDescription />
            <RatingsBlock />
          </div>
          <div className="my-6 lg:hidden">
            <Tracked.ExternalLink
              name="DOWNLOAD_THE_APP"
              href={APPS_FLYER_LINK}
              pagePosition="Body"
              target="_blank"
              rel="noreferrer"
              variant="forest"
            >
              Download now
            </Tracked.ExternalLink>
          </div>
          <div className="flex w-full justify-center bg-dark-background p-6 md:h-[256px]  md:px-6 md:py-4 lg:w-full lg:flex-row">
            <Benefits />
            <div className="hidden w-24 flex-col gap-2 lg:flex">
              <DownloadFromAppStore />
              <StarRating />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export { loader } from './loader.server';
export const meta: MetaFunction = () => {
  return [
    { title: 'Notification Centre' },
    { content: 'noindex', name: 'robots' }
  ];
};
